import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { FeedbackFrontend } from "@performyard/service-contracts";

import { useFeedbackTaskTrackerStore } from "../../../hooks/feedbackTaskTrackerStore";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

import pubSub from "../../../utils/PubSub";

import { FEEDBACK_SENT_EVENT } from "../../../constants/FeedbackEvents";
import { companyFeedbackStreamQuery } from "../../queries/defs/feedback";
import { queryClient } from "../../queryClient";
import {
	CompanyFeedbackCsvQueryParams,
	createFeedback,
	createFeedbackRequest,
	fetchCompanyFeedbackCsv,
	fetchFeedbackByID,
	fetchFeedbackRequestValid,
	fetchHashtags,
	updateFeedback,
	updateFeedbackRequest,
} from "../../services";
import {
	type CompanyFeedbackStreamFilters,
	CompanyFeedbackStreamResult,
	createFeedbackBulk,
	requestFeedbackBulk,
} from "../../services/defs/internal-api/feedback";
import { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";
import { useCurrentUser } from "./auth";

const BULK_FEEDBACK_FEATURE_FLAG = "ff-temp-bulk-feedback-async";

export const useSingleFeedback = (feedbackID: string | undefined, disabled = false) => {
	return useQuery({
		queryKey: ["feedback", feedbackID],
		queryFn: () => (feedbackID ? fetchFeedbackByID(feedbackID) : null),
		meta: {
			errorMessage: "There was an error getting the feedback.",
		},
		enabled: !disabled || !!feedbackID,
	});
};

export const useIsFeedbackRequestValid = (requestedTo: string[] = []) => {
	return useQuery({
		queryKey: ["feedback request valid", requestedTo],
		queryFn: () => fetchFeedbackRequestValid(requestedTo),
		meta: {
			errorMessage: "There was an error getting the feedback.",
		},
	});
};

export const useMutateSingleFeedback = (options?: PYUseMutationOptions) => {
	const client = useQueryClient();
	const { value: bulkEnabled } = useFeatureFlagQuery(BULK_FEEDBACK_FEATURE_FLAG, false);
	const trackTask = useFeedbackTaskTrackerStore((s) => s.track);
	return useMutation({
		mutationKey: ["feedback"],
		mutationFn: (props: {
			id?: string;
			feedbackRequest?: string;
			aboutList: string[];
			content: string;
			feedback_message: string;
			shareWith: string;
		}) => {
			if (props?.id) return updateFeedback(props);
			if (bulkEnabled) {
				return createFeedbackBulk({
					subjectIds: props.aboutList,
					sharedWith: props.shareWith,
					content: props.content,
					feedbackMessage: props.feedback_message,
					feedbackRequestId: props.feedbackRequest,
				});
			}
			return createFeedback(props);
		},
		...options,
		onSuccess: (data, variables, context) => {
			options?.onSuccess && options.onSuccess(data, variables, context);
			// Bulk path returns on enqueue (202) — defer invalidation until the worker finishes,
			// or the refetch races ahead of the write and hides the new feedback.
			if (data && typeof data === "object" && "taskId" in data && data.taskId) {
				trackTask(String(data.taskId), [["search-service", "feedback"], ["feedback"]], [FEEDBACK_SENT_EVENT]);
			} else {
				client.invalidateQueries({ queryKey: ["search-service", "feedback"] });
				client.invalidateQueries({ queryKey: ["feedback"] });
				pubSub.publish(FEEDBACK_SENT_EVENT);
			}
		},
	});
};

export const useMutateSingleFeedbackRequest = (options?: PYUseMutationOptions) => {
	const client = useQueryClient();
	const { value: bulkEnabled } = useFeatureFlagQuery(BULK_FEEDBACK_FEATURE_FLAG, false);
	const trackTask = useFeedbackTaskTrackerStore((s) => s.track);
	return useMutation({
		mutationKey: ["feedback-requests"],
		mutationFn: (props: { id?: string; message?: string; requestedTo?: string[] }) => {
			if (props?.id) return updateFeedbackRequest({ message: props.message, id: props.id });
			if (bulkEnabled)
				return requestFeedbackBulk({ recipientIds: props.requestedTo ?? [], message: props.message });
			return createFeedbackRequest({ message: props.message, requestedTo: props.requestedTo });
		},

		...options,
		onSuccess: (data, variables, context) => {
			options?.onSuccess && options.onSuccess(data, variables, context);
			// Bulk path returns on enqueue (202) — defer invalidation until the worker finishes.
			if (data && typeof data === "object" && "taskId" in data && data.taskId) {
				trackTask(String(data.taskId), [["search-service", "feedback_request"]]);
			} else {
				client.invalidateQueries({ queryKey: ["search-service", "feedback_request"] });
			}
		},
	});
};

export const useCompanyFeedbackCsv = (query: Partial<CompanyFeedbackCsvQueryParams>) => {
	return useQuery({
		queryKey: ["company-feedback-csv", query],
		queryFn: () => fetchCompanyFeedbackCsv(query),
		enabled: false,
		meta: {
			errorMessage: "There was an error getting the company feedback CSV.",
		},
	});
};

export const useCompanyHashtags = () => {
	return useQuery<string[]>({
		queryKey: ["company-hashtags"],
		queryFn: () => fetchHashtags(),
		meta: {
			errorMessage: "There was an error getting the company hashtags",
		},
	});
};

export const useFeedbackStream = (
	filters?: CompanyFeedbackStreamFilters,
	onChunk?: (chunk: { data: FeedbackFrontend[] }) => void,
	options?: PYUseQueryOptions<CompanyFeedbackStreamResult>
) => {
	const { data: currentUser } = useCurrentUser();
	const resetQuery = () => {
		// Remove the cached data entirely to force fresh fetch (needed because of infinite staleTime)
		queryClient.removeQueries({ queryKey: companyFeedbackStreamQuery(filters, onChunk).queryKey });
	};
	const query = useQuery<CompanyFeedbackStreamResult>({
		...companyFeedbackStreamQuery(filters, onChunk),
		...options,
		enabled: !!currentUser && options?.enabled !== false,
	});

	return {
		// eslint-disable-next-line @tanstack/query/no-rest-destructuring -- intentional: we want to expose the full query object alongside resetQuery
		...query,
		resetQuery,
	};
};
